/* GET /api/live, on a loop, for as long as the tab is open.
 *
 * This is the one request the station cannot get wrong. Everything else on the
 * site is browsing; this is the answer to "what will I hear if I press play",
 * and it is asked again every fifteen seconds for hours at a time. So the rules
 * here are about endurance rather than about speed:
 *
 * 1. NOTHING POLLS A HIDDEN TAB. Somebody with the station open in a background
 *    tab for eight hours is 1,920 requests we do not need. The timer is cleared
 *    on `visibilitychange` and — this is the half that people forget — coming
 *    back refetches IMMEDIATELY rather than waiting out the interval. A tab that
 *    shows a two-hour-old show for fifteen seconds after you return to it looks
 *    broken, and it looks broken at exactly the moment somebody is looking.
 *
 * 2. A FAILURE NEVER CLEARS THE LAST GOOD ANSWER. Wi-fi drops in a stairwell.
 *    If a failed poll blanked the player, the bar would flash "we cannot reach
 *    the station" at a listener whose audio is still playing perfectly, because
 *    the stream and the API are different servers and only one of them moved.
 *    On error we keep `live`, set `error`, and back off.
 *
 * 3. THE NEXT POLL LANDS JUST PAST THE NEXT BOUNDARY. A fixed 15s tick means a
 *    show that ends at 20:00 can still read as on air at 20:00:14. Both of the
 *    numbers we need are relative durations the SERVER computed — `elapsed_
 *    seconds` and `seconds_until` — so they carry no clock skew, and a poll can
 *    be aimed one second past the changeover instead of hoping to land near it.
 *
 * Backoff doubles from the normal interval and caps at two minutes, with a
 * little jitter so that every open tab in the city does not retry in lockstep
 * the second our API comes back up.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError, api, isAbortError } from '../api';
import type { LiveState } from '../types';

/** The contract in docs/API.md: "polled every 15s by the persistent player". */
const POLL_MS = 15_000;

/** Aiming at a boundary is allowed to be quick. It is never allowed to be a busy loop. */
const MIN_POLL_MS = 3_000;

/** Four failures in, we are asking every two minutes and not more. */
const BACKOFF_CAP_MS = 120_000;

export interface LiveFeed {
  /** The last answer we got. Survives a failed poll on purpose. */
  live: LiveState | null;
  /** Set when the most recent poll failed. `live` may still be good. */
  error: string | null;
  /** True until the first request settles, either way. Never toggles again. */
  loading: boolean;
  /** Ask again now, and reset the backoff. Wired to the player's retry button. */
  refresh: () => void;
}

/**
 * How long until what is on screen stops being true.
 *
 * Built from durations rather than from timestamps compared against `Date.now`.
 * A laptop whose clock is four minutes fast would otherwise decide every show
 * had already ended and poll at the floor forever.
 */
function boundaryDelayMs(state: LiveState): number {
  let soonest = Number.POSITIVE_INFINITY;

  if (state.now) {
    const length = (Date.parse(state.now.ends_at) - Date.parse(state.now.starts_at)) / 1000;
    if (Number.isFinite(length)) {
      soonest = Math.min(soonest, length - state.now.elapsed_seconds);
    }
  }

  const upcoming = state.next[0];
  if (upcoming && typeof upcoming.seconds_until === 'number') {
    soonest = Math.min(soonest, upcoming.seconds_until);
  }

  if (!Number.isFinite(soonest)) return POLL_MS;

  // One second the far side of the changeover, so the poll sees the new show
  // rather than the last instant of the old one.
  return Math.max(MIN_POLL_MS, Math.min(POLL_MS, (soonest + 1) * 1000));
}

function backoffMs(failures: number): number {
  const step = POLL_MS * 2 ** Math.min(failures - 1, 5);
  const jitter = 0.85 + Math.random() * 0.3;
  return Math.min(BACKOFF_CAP_MS, Math.round(step * jitter));
}

/**
 * What to put on screen. ApiError already carries copy written for a person —
 * "we could not reach the station", "that one is ours, not yours" — so it is
 * used as-is rather than flattened into one generic line.
 */
function describe(error: unknown): string {
  if (error instanceof ApiError) return error.detail;
  return 'We lost contact with the station. The stream itself may still be fine.';
}

interface Feed {
  live: LiveState | null;
  error: string | null;
  loading: boolean;
}

const INITIAL: Feed = { live: null, error: null, loading: true };

export function useLive(): LiveFeed {
  const [feed, setFeed] = useState<Feed>(INITIAL);

  // The escape hatch for `refresh`. Everything else lives inside the effect, as
  // plain closures, because there is exactly one loop per hook and it has no
  // business being reconstructed by a re-render.
  const refreshRef = useRef<() => void>(() => {});

  useEffect(() => {
    let alive = true;
    let timer: number | null = null;
    let inFlight: AbortController | null = null;
    let failures = 0;

    const clear = (): void => {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    };

    const schedule = (delay: number): void => {
      clear();
      // Nobody is looking. The next poll happens when they come back, not on a
      // timer that ran the whole time they were in another tab.
      if (document.hidden) return;
      timer = window.setTimeout(() => {
        timer = null;
        void run();
      }, delay);
    };

    const run = async (): Promise<void> => {
      if (!alive) return;
      clear();

      // A poll that fires while the last one is still out cancels its
      // predecessor rather than racing it — the older answer is the wrong one.
      inFlight?.abort();
      const mine = new AbortController();
      inFlight = mine;

      try {
        const data = await api.getLive(mine.signal);
        if (!alive || mine.signal.aborted) return;
        failures = 0;
        setFeed({ live: data, error: null, loading: false });
        schedule(boundaryDelayMs(data));
      } catch (error) {
        // An aborted request is our own doing. It is not a failure and it must
        // not count towards the backoff.
        if (!alive || isAbortError(error)) return;
        failures += 1;
        setFeed((previous) => ({
          live: previous.live,
          error: describe(error),
          loading: false,
        }));
        schedule(backoffMs(failures));
      } finally {
        if (inFlight === mine) inFlight = null;
      }
    };

    refreshRef.current = () => {
      failures = 0;
      void run();
    };

    const onVisibility = (): void => {
      if (document.hidden) {
        clear();
        return;
      }
      failures = 0;
      void run();
    };

    // Coming back from a dead connection is the same event as coming back to
    // the tab: ask again straight away instead of sitting out the backoff.
    const onOnline = (): void => {
      failures = 0;
      void run();
    };

    const onOffline = (): void => {
      clear();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    void run();

    return () => {
      alive = false;
      clear();
      inFlight?.abort();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      refreshRef.current = () => {};
    };
  }, []);

  const refresh = useCallback(() => {
    refreshRef.current();
  }, []);

  return { live: feed.live, error: feed.error, loading: feed.loading, refresh };
}
